import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert, Box, Button, Chip, IconButton, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography
} from '@mui/material'
import { AddCircleOutline, ArrowBack, EventOutlined, Inventory2Outlined, PersonOutline, OpenInNew } from '@mui/icons-material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import {
  commitBomImport, discardBomImport, getBomLibraryDetail, getBomVersionUsages, readBomLibraryError
} from './bomLibraryApi'
import { BOM_LIBRARY_IMPORT_BEAN } from './bomLibraryImportBean'
import { getRandomUUID } from '../../generic/utilities/randomuuid'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import AlertUI from '../../generic/AlertUI'
import ImportFile from '../../importFile'
import BomVersionStructure from './BomVersionStructure'
import BomCompareDialog from './BomCompareDialog'
import { GetDateNumeric } from '../../generic/DateTimeFunctions'
import useBomLibraryIndexSetting from './useBomLibraryIndexSetting'
import { BOM_STATUS_APPROVED, bomStatusColor, bomStatusLabel, bomStatusLabelKey } from './bomLibraryStatus'
import { translateBomLibraryError } from './bomLibraryErrors'
import { SystemValueChip } from '../../generic/systemValues/SystemValueChip'

/** Index every version created through the detail's importer gets (PO decision 2026-09-09). */
export const IMPORTED_VERSION_INDEX_VALUE = 'APPROVED'

/** The three tabs of the sheet (H134 S34). Their order is the one the mockup fixes. */
export const TAB_LINES = 'lines'
export const TAB_USAGE = 'usage'
export const TAB_HISTORY = 'history'

/**
 * BomLibraryDetail — the BOM sheet, story 134 (H134 S29-S39).
 *
 * It is a PAGE now, not a dialog: it is reached only through the route
 * `/resources/bomLibrary/:code`, and the `code` prop is gone (H134 S29). The listing navigates here.
 *
 * Layout: a header with the code, the current version and its author, then one chip per version, and
 * three tabs with counters — Lines, Usage and History. The selected version governs the WHOLE page
 * (H134 S33).
 *
 * ⚠️ The header says "Current version" as a LABEL (D2), it is not a badge or a state: the current
 * version is simply the one with the highest number, which Sun already resolves.
 *
 * ⚠️ The listing may show this BOM as *Draft* while the chip of its current version says *Approved*
 * (H134 S39). That is D6 as signed: the listing summarises ALL versions and the chip is one version.
 * It is not a bug and the two must NOT be made to agree.
 *
 * ⚠️ History is the versions that already exist (P5). There is NO change log.
 */
const BomLibraryDetail = () => {
  const { code: codeParam } = useParams()
  const code = codeParam || ''
  const { t_ } = useSmartTranslation()
  const navigate = useNavigate()
  const isUserAuthorized = useAuthorization()
  const canEdit = isUserAuthorized('BOM_LIBRARY_EDIT')
  const [AlertElement, showAlert] = AlertUI()
  // PO adjustment 2026-09-10: with USE_WORKFLOW_INDEX off there is no index dimension, so the version
  // label is just "Version N" and no status is painted anywhere (RN-G20, H134 S79).
  const { useIndex } = useBomLibraryIndexSetting()

  // The code comes from the URL, so the header shows it while the rest of the sheet is on its way.
  const [detail, setDetail] = useState({ code, name: '', description: '', versions: [] })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showCompare, setShowCompare] = useState(false)
  const [tab, setTab] = useState(TAB_LINES)
  // H134 S38 — an unknown code is the SERVER's literal, shown in the page itself.
  const [loadError, setLoadError] = useState('')
  const [usageCount, setUsageCount] = useState(null)
  // Scope of the NEXT upload; minted again after every commit attempt so two imports never share one.
  const [importScopeUUID, setImportScopeUUID] = useState(() => getRandomUUID())

  const [detailLoaded, setDetailLoaded] = useState(false)
  const loadDetail = useCallback(() => {
    setLoadError('')
    getBomLibraryDetail(code).then((data) => {
      setDetail(data || { code, name: '', description: '', versions: [] })
      setSelectedIndex(0)
    }).catch((thrown) => {
      const { error } = readBomLibraryError(thrown)
      setLoadError(error || '')
    }).finally(() => setDetailLoaded(true))
  }, [code])

  useEffect(() => { loadDetail() }, [loadDetail])

  const versions = detail.versions || []
  // RN-01.8: N2 returns the versions sorted DESC by number, so the first one IS the current version.
  const selectedVersion = versions[selectedIndex]
  const currentVersion = versions[0]

  // H134 S34 — the Usage tab counter. Asked once per selected version, and the tab's table reads the
  // SAME request (PO, 2026-09-17: the table is mounted at once and shows its own first-load spinner
  // where the rows go; a request of its own would let it paint "no data" before the counter says 0).
  // A failure of N7 must not take the page down: no counter and an empty list (H134 S67).
  const [usages, setUsages] = useState({ bomId: null, request: null })
  useEffect(() => {
    let cancelled = false
    setUsageCount(null)
    if (!selectedVersion) {
      setUsages({ bomId: null, request: null })
      return undefined
    }
    const request = getBomVersionUsages(selectedVersion.bomId).then((list) => list || [], () => null)
    setUsages({ bomId: selectedVersion.bomId, request })
    request.then((list) => { if (!cancelled && list) setUsageCount(list.length) })
    return () => { cancelled = true }
  }, [selectedVersion])

  /**
   * H134 S32 / D7 — the chip label: `V3 · RB01`, or plain `V3` when the client's label is empty.
   * The empty half is OMITTED, never painted as a gap: 362 of the GDELS rows have an empty header.
   */
  const versionChipLabel = (version) => {
    const parts = [version.clientVersion, version.clientRevisionBom].filter(
      (part) => typeof part === 'string' && part.trim() !== ''
    )
    return parts.length ? parts.join(' · ') : t_('Version') + ' ' + version.versionNumber
  }

  // H134 S32 — each chip carries its OWN status, which is the index of that version.
  const versionChipStatus = (version) => (useIndex ? version.indexValue : '')

  // H134 S53 / S55 — an APPROVED version is read-only. A DRAFT keeps editing exactly as it was.
  const isSelectedApproved = () =>
    useIndex && selectedVersion && String(selectedVersion.indexValue).toUpperCase() === BOM_STATUS_APPROVED

  // PO, 2026-09-15 — the notice of S53 promises "create a new version to modify it", and until now
  // the only door was a CSV import: before this story the user just edited and saved, and Sun turned
  // it into version n+1 by itself (`allowedSaveModes` returns NEW_VERSION alone for an APPROVED
  // one). The lock was asked for; losing that path was not. This button is the door the notice
  // promises — it unlocks THIS version's structure and the save lands as a new version, untouched.
  const [unlockedBomId, setUnlockedBomId] = useState(null)
  const isUnlocked = () => selectedVersion && unlockedBomId === selectedVersion.bomId
  const isReadOnly = () => (isSelectedApproved() && !isUnlocked()) || !canEdit

  // N13 right after the upload: version n+1 of this BOM (targetBomId ⇒ name/description inherited),
  // always APPROVED. A rejected commit (E-BOM-05 invalid lines, E-BOM-06 duplicates...) discards
  // the scope (N14) and reports Sun's literal; the user fixes the file and imports again.
  const commitImportedVersion = async () => {
    const scope = importScopeUUID
    setImportScopeUUID(getRandomUUID())
    try {
      await commitBomImport(scope, {
        targetBomId: selectedVersion.bomId,
        code: null,
        name: null,
        description: null,
        indexValue: IMPORTED_VERSION_INDEX_VALUE,
        duplicateResolution: null
      })
      showAlert({ severity: 'success', title: t_('Saved correctly'), message: '' })
      loadDetail()
    } catch (thrown) {
      const { error, context } = readBomLibraryError(thrown)
      const detailLines = Array.isArray(context.invalidLines)
        ? context.invalidLines.map((line) => t_('Line') + ' ' + line.line + ': ' + translateBomLibraryError(t_, line.reason)).join(' · ')
        : ''
      showAlert({ severity: 'error', title: translateBomLibraryError(t_, error || 'Not allowed'), message: detailLines })
      await discardBomImport(scope)
      // PO, 2026-09-15: the upload dialog must NOT congratulate the user for a version that was
      // never written. Handing the rejection back puts Sun's own literal in the dialog the user is
      // looking at, instead of a green there and a red behind it.
      return { error: [translateBomLibraryError(t_, error || 'Not allowed'), detailLines].filter(Boolean).join(' - ') }
    }
  }

  const importParams = [{
    bean: BOM_LIBRARY_IMPORT_BEAN,
    name: t_('BOM library'),
    title: 'New version (import)',
    cancelLabel: 'Close',
    extraParams: new Map([['importScopeUUID', importScopeUUID]]),
    // The dialog stays open after a successful import so several files can be imported in a row
    // (each one becomes a new version); the user leaves with "Close".
    closeDialogOnSuccess: false,
    postImportAction: commitImportedVersion
  }]

  /**
   * H134 S63 — the Usage tab. The four coverage fields of N7 are ADDITIVE: assignments, covered vs
   * required units, and whether it is complete.
   *
   * ⚠️ H134 S62 — the assignments of a linked SUB-workflow are its own row here, never folded into
   * its parent. Sun does not descend into sub-workflows and the editor does: the two numbers differ
   * on purpose (D5). Do not compensate it here.
   *
   * ⚠️ H134 S48 — `requiredUnits` counts only the LEAF lines (D5). It is NOT the total of the Lines
   * footer, which is a flat sum of every line (D4).
   */
  const usageColumns = [
    {
      name: t_('Part reference'),
      field: 'partReference',
      render: (usage) => (typeof usage.partReference === 'string' ? usage.partReference : (usage.partReference?.code || ''))
    },
    { name: t_('Code'), field: 'workflowCode' },
    { name: t_('Name'), field: 'workflowName' },
    { name: t_('Description'), field: 'workflowDescription' },
    { name: t_('Version'), field: 'workflowVersion' },
    { name: t_('Assignments'), field: 'assignmentCount' },
    {
      name: t_('Coverage'),
      field: 'coveredUnits',
      render: (usage) => {
        // H134 S66 — a version with no lines requires nothing: no bar and never "0 of 0" presented
        // as covered.
        if (!usage.requiredUnits) return ''
        return t_('{{covered}} of {{required}} units')
          .replace('{{covered}}', usage.coveredUnits)
          .replace('{{required}}', usage.requiredUnits)
      }
    },
    {
      name: t_('Complete'),
      field: 'coverageComplete',
      render: (usage) => (
        usage.requiredUnits
          ? <Chip size='small' label={usage.coverageComplete ? t_('Complete') : t_('Partial')} color={usage.coverageComplete ? 'success' : 'warning'} />
          : null
      )
    }
  ]

  // H134 S64 — opening the process does NOT demand BOM_LIBRARY_EDIT: it is a read.
  const usageActions = {
    custom: [{
      name: 'Open',
      render: () => (
        <IconButton>
          <OpenInNew />
        </IconButton>
      ),
      onClick: (usage) => navigate('/process/workflow2', { state: { workflowId: usage.workflowId } })
    }]
  }

  // H134 S38 — the code does not exist: the server's literal, in the page, with a way back.
  if (loadError) {
    return (
      <Stack spacing={2} data-testid='bom-library-detail' sx={{ p: 2 }}>
        <Alert severity='error' data-testid='bom-library-detail-error'>{translateBomLibraryError(t_, loadError)}</Alert>
        <Box>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/resources/bomLibrary')}>
            {t_('Back to the BOM library')}
          </Button>
        </Box>
      </Stack>
    )
  }

  // PO, 2026-09-17: while the sheet loads, the page is already there — header with the code, the tabs
  // and the lines table — and the spinner is the one that table shows in place of its rows, as every
  // MIC table does. No full-page spinner, no header with empty fields.

  return (
    <Stack spacing={2} data-testid='bom-library-detail' sx={{ p: 2 }}>
      {AlertElement}

      {/* H134 S30 / S37 — header after the mockup 'MIC Biblioteca de BOMs' (PO, 2026-09-15): a round
          back button, an icon badge, the code in mono next to the muted name, and one uppercase meta
          line with the current version and its author. The listing reloads on its own when it mounts. */}
      <Stack direction='row' spacing={2} alignItems='flex-start' data-testid='bom-library-detail-header'>
        <IconButton
          onClick={() => navigate('/resources/bomLibrary')}
          data-testid='bom-library-detail-back'
          title={t_('Back to the BOM library')}
          sx={{ border: 1, borderColor: 'divider', flex: '0 0 auto' }}
        >
          <ArrowBack />
        </IconButton>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            border: 1,
            borderColor: 'divider',
            color: 'primary.main'
          }}
        >
          <Inventory2Outlined />
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Stack direction='row' spacing={1.5} alignItems='baseline' flexWrap='wrap' useFlexGap>
            <Typography variant='h5' sx={{ fontFamily: 'monospace', fontWeight: 800 }}>{detail.code}</Typography>
            <Typography variant='subtitle1' color='text.secondary'>{detail.name}</Typography>
          </Stack>
          {detail.description && (
            <Typography variant='body2' color='text.secondary'>{detail.description}</Typography>
          )}
          {/* PO, 2026-09-15: the meta line as chips — the uppercase caption of the mockup read as one
              grey word on this theme. Same chip language as the version selector below. */}
          {currentVersion && (
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{ mt: 1 }}>
              <Chip
                size='small'
                color='primary'
                variant='outlined'
                data-testid='bom-library-detail-current'
                label={t_('Current version') + ': ' + currentVersion.versionNumber}
              />
              {/* D8 — "Created by", never "Responsible". It tolerates being absent. */}
              <Chip
                size='small'
                variant='outlined'
                icon={<PersonOutline />}
                data-testid='bom-library-detail-author'
                label={t_('Created by') + ': ' + (currentVersion.createdBy || '')}
              />
              <Chip size='small' variant='outlined' icon={<EventOutlined />} label={GetDateNumeric(currentVersion.createdAt)} />
            </Stack>
          )}
        </Box>
      </Stack>

      {/* H134 S32 / S33 — one chip per version; the selected one governs the whole page. */}
      <Stack
        direction='row'
        spacing={1}
        flexWrap='wrap'
        useFlexGap
        data-testid='bom-library-detail-versions'
      >
        {versions.map((version, index) => (
          <Chip
            key={version.bomId}
            clickable
            variant={index === selectedIndex ? 'filled' : 'outlined'}
            color={index === selectedIndex ? 'primary' : bomStatusColor(versionChipStatus(version))}
            onClick={() => setSelectedIndex(index)}
            label={
              versionChipLabel(version) +
              (useIndex && bomStatusLabel(version.indexValue, t_) ? ' · ' + bomStatusLabel(version.indexValue, t_) : '') +
              ' · ' + version.componentCount + ' ' + t_('Lines')
            }
          />
        ))}
      </Stack>

      {(selectedVersion || !detailLoaded) && (
        <>
          <Stack direction='row' spacing={2} alignItems='center' sx={{ flexShrink: 0 }}>
            {/* With a single version there is nothing to compare against (Sun would answer 404). */}
            <Button onClick={() => setShowCompare(true)} disabled={versions.length < 2}>{t_('Compare')}</Button>
            {/* H134 S71 — importing from the sheet is EXACTLY as it was (P3). */}
            {canEdit && selectedVersion && <ImportFile beanParams={importParams} />}
          </Stack>

          {/* H134 S34 — three tabs, each with its counter (none until it is known). */}
          <Tabs
            value={tab}
            onChange={(event, next) => setTab(next)}
            data-testid='bom-library-detail-tabs'
          >
            <Tab value={TAB_LINES} label={t_('Lines') + (selectedVersion ? ' (' + (selectedVersion.componentCount || 0) + ')' : '')} />
            <Tab value={TAB_USAGE} label={t_('Usage') + (usageCount === null ? '' : ' (' + usageCount + ')')} />
            <Tab value={TAB_HISTORY} label={t_('History') + (detailLoaded ? ' (' + versions.length + ')' : '')} />
          </Tabs>

          {tab === TAB_LINES && (
            <Box data-testid='bom-library-detail-lines'>
              {/* H134 S53 — an approved version says WHY it cannot be edited and how to move on. */}
              {/* PO, 2026-09-15: once unlocked there was no way back. While editing as a new version the
                  notice stays, says what will happen on save, and offers to cancel (which locks again). */}
              {isSelectedApproved() && isUnlocked() && (
                <Alert
                  severity='info'
                  data-testid='bom-library-detail-unlocked'
                  sx={{ mb: 2, alignItems: 'center' }}
                  action={(
                    <Button
                      size='small'
                      variant='outlined'
                      data-testid='bom-library-detail-relock'
                      onClick={() => setUnlockedBomId(null)}
                    >
                      {t_('Cancel')}
                    </Button>
                  )}
                >
                  {t_('The changes will be saved as a new version')}
                </Alert>
              )}
              {isSelectedApproved() && !isUnlocked() && (
                <Alert
                  severity='info'
                  data-testid='bom-library-detail-readonly'
                  sx={{ mb: 2, alignItems: 'center' }}
                  action={canEdit
                    ? (
                      <Button
                        size='small'
                        variant='outlined'
                        startIcon={<AddCircleOutline />}
                        data-testid='bom-library-detail-unlock'
                        onClick={() => setUnlockedBomId(selectedVersion.bomId)}
                      >
                        {t_('Create a new version from this one')}
                      </Button>
                      )
                    : null}
                >
                  {t_('This version is approved and cannot be edited')}
                  {canEdit ? ' — ' + t_('To modify it, create a new version') : ''}
                </Alert>
              )}
              <BomVersionStructure
                bomId={selectedVersion?.bomId}
                version={selectedVersion}
                onSaved={loadDetail}
                readOnly={isReadOnly()}
              />
            </Box>
          )}

          {tab === TAB_USAGE && (
            <Box data-testid='bom-library-detail-usages'>
              {/* H134 S65 — a version nobody uses says so in words: with zero usages the table is
                  unmounted and the message takes its place. Until the count is known the table is
                  there, spinning where its rows go (PO, 2026-09-17), fed by the counter's request. */}
              {usageCount === 0
                ? (
                  <Typography variant='body2' color='text.secondary' data-testid='bom-library-detail-usages-empty'>
                    {t_('No process uses this version yet')}
                  </Typography>
                  )
                : usages.request && (
                  <SmartMateriaUITable
                    key={usages.bomId}
                    columns={usageColumns}
                    actions={usageActions}
                    dataFetch={() => usages.request.then((list) => list || [])}
                    primaryKeys={['workflowId']}
                    searchAndPaginationVisible='none'
                    tablePageSize={Number.MAX_SAFE_INTEGER}
                    sticky={false}
                  />
                )}
            </Box>
          )}

          {/* H134 S35 — the History IS the versions that already exist (P5). No change log. */}
          {tab === TAB_HISTORY && (
            <Box data-testid='bom-library-detail-history'>
              {/* H134 S35 — the history IS the versions that exist (P5): one row each, newest first,
                  with the same status chip the version selector paints. */}
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t_('Version')}</TableCell>
                      {useIndex && <TableCell>{t_('Status')}</TableCell>}
                      <TableCell align='right'>{t_('Lines')}</TableCell>
                      <TableCell>{t_('Created by')}</TableCell>
                      <TableCell>{t_('Created')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versions.map((version, index) => (
                      <TableRow
                        key={version.bomId}
                        hover
                        selected={index === selectedIndex}
                        onClick={() => setSelectedIndex(index)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                          {versionChipLabel(version)}
                        </TableCell>
                        {useIndex && (
                          <TableCell><SystemValueChip look='outlined' domain='FeatureGroupRevisionState' value={bomStatusLabelKey(version.indexValue)} color={bomStatusColor(version.indexValue)} testId='bom-status-chip' /></TableCell>
                        )}
                        <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums' }}>{version.componentCount}</TableCell>
                        <TableCell>{version.createdBy || ''}</TableCell>
                        <TableCell>{GetDateNumeric(version.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {showCompare && selectedVersion && (
            <BomCompareDialog
              toBomId={selectedVersion.bomId}
              versions={versions}
              onClose={() => setShowCompare(false)}
            />
          )}
        </>
      )}
    </Stack>
  )
}

export default BomLibraryDetail
